import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
} from '@mui/material';
import {
  BeachAccess,
  Layers,
  ArrowBack,
  Settings,
  Visibility,
  Security,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import {
  VACACIONES,
  EXTRAS_ORGANIGRAMA,
} from 'Shared/Routing/Application/Constants/PathsConstant';
import {
  PORTAL_USER,
} from 'User/Application/Constants/PathsConstant';
import {
  PRIVATE_USER_ORGCHART_VACACIONES,
  PRIVATE_USER_ORGCHART_EXTRAS,
} from 'OrgChart/Application/Constants/PathsConstant';
import {
  ADMIN_ORGCHART,
  ORGCHART,
  EXTRAS_VALIDATOR,
} from 'Shared/Security/Application/Constants/PermissionsConstant';

const ValidationOrgChartsView = () => {
  const navigate = useNavigate();
  const { isAdmin, checkPermission } = useAuthContext();

  const canAdminOrgChart = isAdmin() || checkPermission(ADMIN_ORGCHART) || checkPermission(ORGCHART);
  const hasValidationPermission = canAdminOrgChart || checkPermission(EXTRAS_VALIDATOR);

  useEffect(() => {
    if (!hasValidationPermission) {
      navigate(PORTAL_USER, { replace: true });
    }
  }, [hasValidationPermission, navigate]);

  if (!hasValidationPermission) {
    return null;
  }

  const orgCharts = [
    {
      id: 'vacaciones',
      title: 'Organigrama de Vacaciones',
      subtitle: 'Validación de Vacaciones',
      description: 'Estructura organizativa y flujo de aprobación de solicitudes y ausencias del personal.',
      icon: <BeachAccess sx={{ fontSize: '2rem', color: '#0284c7' }} />,
      iconBg: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
      iconBorder: '#7dd3fc',
      viewPath: VACACIONES,
      adminPath: canAdminOrgChart ? PRIVATE_USER_ORGCHART_VACACIONES : null,
      badge: 'Vacaciones',
    },
    {
      id: 'extras',
      title: 'Organigrama de Extras',
      subtitle: 'Validación de Extras',
      description: 'Jerarquía y validadores asignados para la aprobación de horas y conceptos extraordinarios.',
      icon: <Layers sx={{ fontSize: '2rem', color: '#6366f1' }} />,
      iconBg: 'linear-gradient(135deg, #e0e7ff 0%, #ede9fe 100%)',
      iconBorder: '#c7d2fe',
      viewPath: EXTRAS_ORGANIGRAMA,
      adminPath: canAdminOrgChart ? PRIVATE_USER_ORGCHART_EXTRAS : null,
      badge: 'Extras',
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: '#f1f5f9',
        minHeight: '100%',
        width: '100%',
      }}
    >
      <Box sx={{ maxWidth: '1440px', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3.5 }}>
        {/* Top Header Card */}
        <Box
          sx={{
            borderRadius: '24px',
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            p: { xs: 2.5, sm: 3.5, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Back button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(PORTAL_USER)}
              sx={{
                color: '#257eb9',
                fontWeight: 700,
                fontSize: '0.85rem',
                textTransform: 'none',
                borderRadius: '12px',
                px: 1.5,
                '&:hover': { bgcolor: '#f0f9ff' },
              }}
            >
              Volver al Portal
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  icon={<Security sx={{ fontSize: '1rem', color: '#257eb9 !important' }} />}
                  label="ÁREA RESTRINGIDA DE VALIDACIONES"
                  sx={{
                    bgcolor: '#e0f2fe',
                    color: '#0369a1',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: 0.5,
                    borderRadius: '12px',
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#1e293b',
                  fontSize: { xs: '1.5rem', sm: '1.85rem' },
                  letterSpacing: -0.5,
                }}
              >
                Organigramas de Validaciones
              </Typography>
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  mt: 0.5,
                  maxWidth: '700px',
                }}
              >
                Selecciona la estructura organizativa de validación que deseas consultar o administrar.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Cards Grid */}
        <Grid container spacing={3}>
          {orgCharts.map((item) => (
            <Grid item key={item.id} xs={12} sm={6} md={6}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: '20px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 18px rgba(0,0,0,0.04)',
                  bgcolor: '#ffffff',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(37, 126, 185, 0.15)',
                    borderColor: '#93c5fd',
                  },
                }}
              >
                <div>
                  {/* Top row: Icon + Badge */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        background: item.iconBg,
                        border: `1px solid ${item.iconBorder}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Chip
                      label={item.badge}
                      size="small"
                      sx={{
                        bgcolor: '#f1f5f9',
                        color: '#475569',
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        height: '24px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>

                  {/* Title & Description */}
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.05rem',
                      color: '#1e293b',
                      lineHeight: 1.3,
                      mb: 0.8,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      mb: 2.5,
                    }}
                  >
                    {item.description}
                  </Typography>
                </div>

                {/* Actions */}
                <Box
                  sx={{
                    pt: 2,
                    borderTop: '1px solid #f1f5f9',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Visibility fontSize="small" />}
                    onClick={() => navigate(item.viewPath)}
                    sx={{
                      bgcolor: '#257eb9',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      textTransform: 'none',
                      borderRadius: '10px',
                      px: 2,
                      py: 0.8,
                      flex: 1,
                      boxShadow: '0 2px 8px rgba(37, 126, 185, 0.25)',
                      '&:hover': { bgcolor: '#1d6594' },
                    }}
                  >
                    Ver Organigrama
                  </Button>

                  {item.adminPath && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Settings fontSize="small" />}
                      onClick={() => navigate(item.adminPath)}
                      sx={{
                        color: '#257eb9',
                        borderColor: '#93c5fd',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 1.5,
                        py: 0.8,
                        '&:hover': {
                          borderColor: '#257eb9',
                          bgcolor: '#f0f9ff',
                        },
                      }}
                    >
                      Administrar
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default ValidationOrgChartsView;
